// MessageBubbleUpdated.tsx - Refactored with fixed positioning and timestamps
import React, { ReactNode, useState, useCallback, useRef, useEffect } from 'react';
import { Anchorme } from "react-anchorme";
import './ConversationBody.css'; // NEW CSS - don't import old MessageBubbleRedux.css
import moment from 'moment';
import { Reply as ReplyIcon, Smile as ReactIcon, FileText, Phone, ExternalLink, X, Check, CheckCheck, Clock } from 'lucide-react';
import logger, { Logger } from '../../../utils/logger';

// Redux imports
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import {
  selectCurrentUser,
  selectWhatsappIntegration
} from '../../../store/slices/userSlice';
import {
  selectSelectedConversation
} from '../../../store/slices/conversationsSlice';
import {
  Message,
  sendReaction,
  setReplyContext,
  retryMessage
} from '../../../store/slices/messagesSlice';
import {
  openMediaPreview,
  selectIsMediaCached,
  selectMediaCacheItem,
  downloadMedia
} from '../../../store/slices/mediaSlice';
import { store } from '../../../store/store';

// Component imports
import { MessageMedia } from './MessageMedia';
import MessageStatusTimeline from './MessageStatusTimeline';

// Utility imports
import {
  extractMessageContent,
  formatMessageTime,
  isOutgoingMessage,
  isMediaMessage
} from '../utils/messageFormatterUtil';
import toast from 'react-hot-toast';

interface MessageBubbleProps {
  message: Message;
  isFirstInGroup?: boolean;
  isLastInGroup?: boolean;
  showTimestamp?: boolean;
}

export const MessageBubbleUpdated: React.FC<MessageBubbleProps> = ({
  message,
  isFirstInGroup = true,
  isLastInGroup = true,
  showTimestamp = true,
}) => {
  const dispatch = useAppDispatch();

  // Redux state
  const currentUser = useAppSelector(selectCurrentUser);
  const whatsappIntegration = useAppSelector(selectWhatsappIntegration);
  const selectedConversation = useAppSelector(selectSelectedConversation);
  const isMediaCached = useAppSelector(selectIsMediaCached(message._id));
  const cachedMedia = useAppSelector(isMediaCached ?
    selectMediaCacheItem(message._id) :
    () => null
  );

  // Local UI state
  const [showEmojiBar, setShowEmojiBar] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [isMessageExpanded, setIsMessageExpanded] = useState(false);

  // Determine if message is outgoing
  const isOutgoing = isOutgoingMessage(message);

  // 🔍 Debug logging for failed messages with error
  useEffect(() => {
    if (message.status === 'failed') {
      logger.log('🔴 MessageBubbleUpdated - Failed message:', {
        messageId: message._id,
        status: message.status,
        hasError: !!message.error,
        errorLength: message.error?.length || 0,
        errorData: message.error,
        fullMessage: message
      });
    }
  }, [message]);

  // Quick emoji options for reactions
  const quickEmojis = ['👍', '❤️', '😂', '😮', '😢', '😭'];

  // Close emoji bar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowEmojiBar(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Format message content with auto-collapse
  const formatContent = useCallback((text: any): ReactNode[] => {
    const messageText = extractMessageContent(text);
    const CHARACTER_LIMIT = 150;
    const isLongMessage = messageText.length > CHARACTER_LIMIT;

    let displayText = messageText;

    if (isLongMessage && !isMessageExpanded) {
      let truncateAt = CHARACTER_LIMIT;
      while (truncateAt > 0 && messageText[truncateAt] !== ' ' && messageText[truncateAt] !== '\n') {
        truncateAt--;
      }
      if (truncateAt === 0) truncateAt = CHARACTER_LIMIT;
      displayText = messageText.substring(0, truncateAt).trim();
    }

    const formattedContent = displayText.split('\n').map((line: string, i: number, arr: string[]) => (
      <React.Fragment key={i}>
        <Anchorme target="_blank">
          {line || ' '}
        </Anchorme>
        {i < arr.length - 1 && <br />}
      </React.Fragment>
    ));

    if (isLongMessage && !isMessageExpanded) {
      formattedContent.push(
        <React.Fragment key="read-more">
          <br />
          <button
            className="read-more-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsMessageExpanded(true);
            }}
          >
            read more
          </button>
        </React.Fragment>
      );
    }

    return formattedContent;
  }, [isMessageExpanded, message._id]);

  // Handle emoji reactions
  const handleEmojiClick = useCallback((emoji: string) => {
    logger.log('🚀 handleEmojiClick called!', { emoji, messageId: message._id });

    if (message.messageId || message._id) {
      dispatch(sendReaction({
        messageId: message.messageId || message._id,
        emoji,
        phone: selectedConversation?.mobile || message.phone || ''
      }));
    }

    setShowEmojiBar(false);
  }, [dispatch, message, selectedConversation]);

  // Handle reply
  const handleReplyClick = useCallback(() => {
    dispatch(setReplyContext(message));
  }, [dispatch, message]);

  // Handle retry failed message
  const handleRetry = useCallback(async () => {
    if (message._id) {
      logger.log('🔄 Retry clicked for message:', message._id);
      try {
        await dispatch(retryMessage(message._id)).unwrap();
        toast.success("Message resent successfully");
      } catch (error) {
        logger.log('❌ Retry failed:', error);
        toast.error("Failed to resend message");
      }
    }
  }, [dispatch, message._id]);

  // Handle media download
  const handleMediaDownload = useCallback((): Promise<string | null> => {
    return new Promise<string | null>((resolve, reject) => {
      if (message._id && isMediaMessage(message)) {
        if (isMediaCached && cachedMedia?.url) {
          resolve(cachedMedia.url);
          return;
        }

        dispatch(downloadMedia(message))
          .unwrap()
          .then(() => {
            const state = store.getState();
            const updatedMedia = selectMediaCacheItem(message._id)(state);
            if (updatedMedia?.url) {
              resolve(updatedMedia.url);
            } else {
              resolve(null);
            }
          })
          .catch((error) => {
            reject(error);
          });
      } else {
        resolve(null);
      }
    });
  }, [dispatch, message, isMediaCached, cachedMedia]);

  const handleMediaPreview = useCallback(() => {
    if (message._id) {
      if (isMediaCached) {
        dispatch(openMediaPreview(message._id));
      } else {
        handleMediaDownload()
          .then(() => {
            setTimeout(() => {
              dispatch(openMediaPreview(message._id));
            }, 100);
          })
          .catch(error => {
            toast.error("Failed to load media preview");
          });
      }
    }
  }, [dispatch, message._id, isMediaCached, handleMediaDownload]);

  // Helper function to render reply context - SIMPLIFIED
  const renderReplyContext = useCallback((contextObj: any): ReactNode => {
    // If no context, bail out
    if (!contextObj || !contextObj.message) {
      return null;
    }

    logger.log('📨 CONTEXT FOUND:', contextObj);

    const msg = contextObj.message;
    let displayText = '';

    // Extract text from whatever structure we have
    if (typeof msg.body === 'string') {
      displayText = msg.body;
    } else if (msg.body?.text?.body) {
      displayText = msg.body.text.body;
    } else if (msg.text?.body) {
      displayText = msg.text.body;
    } else {
      // Last resort - try to show something
      displayText = JSON.stringify(msg).substring(0, 100);
    }

    // Truncate if too long
    const maxLength = 100;
    if (displayText.length > maxLength) {
      displayText = displayText.substring(0, maxLength) + '...';
    }

    logger.log('✅ RENDERING CONTEXT:', displayText.substring(0, 50));

    return (
      <div className="reply-context-container">
        <span className="reply-context-label">↩️ Replying to</span>
        <div className="reply-context-text">
          {displayText}
        </div>
      </div>
    );
  }, []);

  const renderContent = useCallback((): ReactNode => {
    // For reaction messages
    if (message?.messageType === "reaction") {
      return formatContent(message.message.emoji || '');
    }

    // For button response messages
    if (message?.messageType === "button") {
      return (
        <div className="button-response-container">
          <div className="button-response-indicator">
            <span className="button-icon">📱</span>
            <span className="button-label">Button Response:</span>
          </div>
          <div className="button-response-text">
            {message.message?.button?.text || message.message?.button?.payload || 'Button clicked'}
          </div>
          {message.context && renderReplyContext(message.context)}
        </div>
      );
    }

    // For location messages
    if (message?.messageType === "location") {
      const coords = message.message?.coords;
      const address = message.message?.address;
      const latitude = coords?.latitude;
      const longitude = coords?.longitude;

      // Generate Google Maps link and static map preview
      const googleMapsUrl = latitude && longitude
        ? `https://www.google.com/maps?q=${latitude},${longitude}`
        : null;

      const staticMapUrl = latitude && longitude
        ? `https://maps.googleapis.com/maps/api/staticmap?center=${latitude},${longitude}&zoom=15&size=400x200&markers=color:red%7C${latitude},${longitude}&key=YOUR_API_KEY`
        : null;

      // Copy handlers
      const handleCopyAddress = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (address) {
          navigator.clipboard.writeText(address).then(() => {
            toast.success('Address copied!');
          }).catch(() => {
            toast.error('Failed to copy address');
          });
        }
      };

      const handleCopyCoordinates = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (latitude && longitude) {
          const coordsText = `${latitude}, ${longitude}`;
          navigator.clipboard.writeText(coordsText).then(() => {
            toast.success('Coordinates copied!');
          }).catch(() => {
            toast.error('Failed to copy coordinates');
          });
        }
      };

      return (
        <div className="location-message-container">
          {/* Show reply context if available */}
          {message.context && renderReplyContext(message.context)}

          <div className="location-header">
            <span className="location-icon">📍</span>
            <span className="location-label">Location Shared</span>
          </div>

          {/* Map Preview */}
          {latitude && longitude && (
            <div className="location-map-preview">
              <div className="location-map-placeholder">
                <div className="map-pin-icon">📍</div>
                <div className="map-coordinates-overlay">
                  {latitude.toFixed(4)}°, {longitude.toFixed(4)}°
                </div>
              </div>
            </div>
          )}

          {/* Address */}
          {address && (
            <div className="location-address">
              <div className="location-address-text">{address}</div>
              <button
                className="location-copy-btn"
                onClick={handleCopyAddress}
                title="Copy address"
              >
                📋
              </button>
            </div>
          )}

          {/* Coordinates */}
          {latitude && longitude && (
            <div className="location-coords">
              <span className="coords-label">Coordinates:</span>
              <span className="coords-value">{latitude.toFixed(6)}, {longitude.toFixed(6)}</span>
              <button
                className="location-copy-btn"
                onClick={handleCopyCoordinates}
                title="Copy coordinates"
              >
                📋
              </button>
            </div>
          )}

          {/* Action Buttons */}
          <div className="location-actions">
            {googleMapsUrl && (
              <a
                href={googleMapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="location-action-btn location-btn-primary"
              >
                <ExternalLink size={16} />
                <span>Open in Google Maps</span>
              </a>
            )}

            {latitude && longitude && (
              <a
                href={`https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="location-action-btn location-btn-secondary"
              >
                <span>🧭</span>
                <span>Get Directions</span>
              </a>
            )}
          </div>
        </div>
      );
    }

    // For unsupported message types
    if (message?.messageType === "unsupported") {
      const errorInfo = message.message?.errors?.[0];
      const errorTitle = errorInfo?.title || "Message type unknown";

      return (
        <div className="unsupported-message-container">
          {/* Show reply context if available */}
          {message.context && renderReplyContext(message.context)}

          <div className="unsupported-header">
            <span className="unsupported-icon">⚠️</span>
            <span className="unsupported-label">Message Not Supported</span>
          </div>

          <div className="unsupported-text">
            This message type is not supported by the WhatsApp API
          </div>

          {errorTitle && errorTitle !== "Message type unknown" && (
            <div className="unsupported-detail">
              {errorTitle}
            </div>
          )}
        </div>
      );
    }

    // For contact messages
    if (message?.messageType === "contacts") {
      const contacts = message.message?.contacts || [];

      // VCF download handler
      const downloadVCF = (contact: any) => {
        const fullName = contact.name?.formatted_name ||
          `${contact.name?.first_name || ''} ${contact.name?.middle_name || ''} ${contact.name?.last_name || ''}`.trim() ||
          'Unknown Contact';
        const firstName = contact.name?.first_name || '';
        const middleName = contact.name?.middle_name || '';
        const lastName = contact.name?.last_name || '';

        let vcf = 'BEGIN:VCARD\nVERSION:3.0\n';
        vcf += `FN:${fullName}\nN:${lastName};${firstName};${middleName};;\n`;

        if (contact.phones && contact.phones.length > 0) {
          contact.phones.forEach((phoneObj: any) => {
            const type = phoneObj.type ? phoneObj.type.toUpperCase() : 'CELL';
            vcf += `TEL;TYPE=${type}:${phoneObj.phone}\n`;
          });
        }

        vcf += 'END:VCARD';
        const blob = new Blob([vcf], { type: 'text/vcard' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const fileName = contact.name?.formatted_name || `${contact.name?.first_name || 'contact'}`.replace(/\s+/g, '_');
        link.download = `${fileName}.vcf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Contact downloaded successfully!');
      };

      const copyPhone = (phone: string) => {
        navigator.clipboard.writeText(phone).then(() => {
          toast.success('Phone number copied!');
        }).catch(() => {
          toast.error('Failed to copy phone number');
        });
      };

      return (
        <div className="contact-message-container">
          {/* Show reply context if available */}
          {message.context && renderReplyContext(message.context)}

          <div className="contact-header">
            <span className="contact-icon">👤</span>
            <span className="contact-label">Shared Contact{contacts.length > 1 ? 's' : ''}</span>
          </div>
          {contacts.map((contact: any, index: number) => {
            const fullName = contact.name?.formatted_name ||
              `${contact.name?.first_name || ''} ${contact.name?.middle_name || ''} ${contact.name?.last_name || ''}`.trim() ||
              'Unknown Contact';
            const phones = contact.phones || [];

            return (
              <div key={`contact-${index}`} className="contact-card">
                <div className="contact-avatar">
                  <span>{fullName.charAt(0).toUpperCase()}</span>
                </div>
                <div className="contact-details">
                  <div className="contact-name">{fullName}</div>
                  {phones.map((phoneObj: any, phoneIndex: number) => (
                    <div key={`phone-${phoneIndex}`} className="contact-phone">
                      <Phone size={14} />
                      <span>{phoneObj.phone}</span>
                      {phoneObj.type && <span className="phone-type">({phoneObj.type})</span>}
                      <button className="copy-phone-btn" onClick={(e) => { e.stopPropagation(); copyPhone(phoneObj.phone); }} title="Copy phone number">📋</button>
                    </div>
                  ))}
                </div>
                <div className="contact-actions">
                  <button className="contact-action-btn" onClick={(e) => { e.stopPropagation(); downloadVCF(contact); }} title="Download VCF">
                    <ExternalLink size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    // For template messages
    if (message?.messageType === "template") {
      return (
        <div className="template-message-container">
          {/* Show reply context if available */}
          {message.context && renderReplyContext(message.context)}

          {/* Render header if present (document, image, video, etc.) */}
          {message.message?.header && !Array.isArray(message.message.header) && message.message.header.parameters?.[0] && (() => {
            const headerParam = message.message.header.parameters![0];

            if (headerParam.type === 'document' && headerParam.document) {
              return (
                <div className="template-header template-document">
                  <FileText size={20} />
                  <a
                    href={headerParam.document.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="template-document-link"
                  >
                    📄 {headerParam.document.filename || 'Download Document'}
                  </a>
                </div>
              );
            }

            if (headerParam.type === 'image' && headerParam.image) {
              return (
                <div className="template-header template-image">
                  <img src={headerParam.image.link} alt="Template header" />
                </div>
              );
            }

            if (headerParam.type === 'video' && headerParam.video) {
              return (
                <div className="template-header template-video">
                  <video controls src={headerParam.video.link} />
                </div>
              );
            }

            return null;
          })()}

          {/* Template body */}
          {message.message?.body && typeof message.message.body === "object" && message.message.body?.text?.body && (
            <div className="template-body">
              {formatContent(message.message.body.text.body)}
            </div>
          )}

          {/* Render footer if present */}
          {message.message?.footer && message.message.footer.length > 0 && (
            <div className="template-footer">
              {message.message.footer.map((footerItem: any, idx: number) => (
                <div key={idx} className="template-footer-text">
                  {footerItem.text}
                </div>
              ))}
            </div>
          )}

          {/* Render buttons if present */}
          {message.message?.buttons && message.message.buttons.length > 0 && (
            <div className="template-buttons-container">
              {message.message.buttons.map((buttonGroup: any, groupIdx: number) => {
                if (buttonGroup.buttons && Array.isArray(buttonGroup.buttons)) {
                  return buttonGroup.buttons.map((button: any, btnIdx: number) => {
                    // URL Button
                    if (button.type === 'URL') {
                      return (
                        <a
                          key={`${groupIdx}-${btnIdx}`}
                          href={button.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="template-button template-button-url"
                        >
                          <ExternalLink size={16} />
                          <span>{button.text}</span>
                        </a>
                      );
                    }

                    // Quick Reply Button (disabled for outgoing, since user already sent it)
                    if (button.type === 'QUICK_REPLY') {
                      return (
                        <button
                          key={`${groupIdx}-${btnIdx}`}
                          className="template-button template-button-reply"
                          disabled={isOutgoing}
                          style={{
                            opacity: isOutgoing ? 0.5 : 1,
                            cursor: isOutgoing ? 'not-allowed' : 'pointer'
                          }}
                          title={isOutgoing ? 'Already sent' : button.text}
                        >
                          <span>{button.text}</span>
                        </button>
                      );
                    }

                    // Call Button
                    if (button.type === 'PHONE_NUMBER') {
                      return (
                        <a
                          key={`${groupIdx}-${btnIdx}`}
                          href={`tel:${button.phone_number}`}
                          className="template-button template-button-call"
                        >
                          <Phone size={16} />
                          <span>{button.text}</span>
                        </a>
                      );
                    }

                    return null;
                  });
                }
                return null;
              })}
            </div>
          )}
        </div>
      );
    }

    // For media messages
    if (isMediaMessage(message)) {
      const mediaMessage = {
        _id: message._id,
        messageType: message.messageType as 'video' | 'image' | 'document' | 'audio',
        downloadUrl: cachedMedia?.url || message.downloadUrl,
        message: {
          body: extractMessageContent(message.message),
          id: message.message?.id,
          mime_type: message.message?.mime_type,
          filename: message.message?.filename || 'file'
        }
      };

      return (
        <div>
          {/* Show reply context if available */}
          {message.context && renderReplyContext(message.context)}

          <MessageMedia
            message={mediaMessage}
            onDownload={handleMediaDownload}
            onPreview={handleMediaPreview}
            integrationInfo={whatsappIntegration}
          />
        </div>
      );
    }

    // For regular text messages
    return (
      <div className="message-text-content">
        {/* Show reply context if available */}
        {message.context && renderReplyContext(message.context)}

        {formatContent(message.message)}
      </div>
    );
  }, [message, formatContent, cachedMedia, handleMediaDownload, handleMediaPreview, whatsappIntegration, renderReplyContext]);

  // Filter out standalone reaction messages
  if (message.messageType === 'reaction') {
    logger.log('🚫 BLOCKING standalone reaction render:', {
      messageId: message._id,
      messageType: message.messageType
    });
    return null;
  }

  return (
    <div
      className={`message-bubble-row ${isOutgoing ? 'outgoing' : 'incoming'}`}
      onMouseEnter={() => !isOutgoing && setShowActions(true)}
      onMouseLeave={() => {
        setShowActions(false);
        setShowEmojiBar(false);
      }}
    >
      <div
        className="message-bubble-container"
        style={{
          position: 'relative',
          maxWidth: '65%',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Sender name for outgoing messages */}
        {isFirstInGroup && isOutgoing && (
          <div className="message-sender">
            <span>{currentUser?.firstName || 'You'}</span>
          </div>
        )}

        <div className="message-bubble-content">
          {renderContent()}

          {/* Display reaction if message has one */}
          {message.message?.reaction && (
            <div className="message-reaction">
              <span>Reacted:</span>
              <span>{message.message.reaction.emoji}</span>
            </div>
          )}

          {/* Always show timestamp for better UX */}
          {showTimestamp && (
            <div className="message-footer">
              <span className="message-time">
                {formatMessageTime(message.createdAt)}
              </span>

              {/* Message status timeline for outgoing messages */}
              {isOutgoing && (
                <MessageStatusTimeline
                  status={message.status}
                  statusLogs={message.statusLogs}
                  messageId={message._id}
                  onRetry={handleRetry}
                  error={message.error}
                />
              )}
            </div>
          )}
        </div>

        {/* Reply and React buttons - Only for incoming messages */}
        {!isOutgoing && (
        <div
          className="message-actions"
          style={{
            position: 'absolute',
            left: '100%', // Right edge of the bubble-container
            marginLeft: '8px', // 8px gap
            top: '50%',
            transform: 'translateY(-50%)',
            display: 'flex',
            flexDirection: 'column',
            gap: '6px',
            opacity: showActions ? 1 : 0,
            visibility: showActions ? 'visible' : 'hidden',
            transition: 'opacity 0.3s ease, visibility 0.3s ease',
            zIndex: 100,
            pointerEvents: showActions ? 'auto' : 'none'
          }}
        >
          <button
            className="message-action-button reply-button"
            onClick={(e) => {
              logger.log('📤 REPLY BUTTON CLICKED!', { messageId: message._id });
              e.stopPropagation();
              handleReplyClick();
            }}
            title="Reply"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%)',
              border: '2px solid #4177bd',
              padding: '6px',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#4177bd',
              boxShadow: '0 2px 8px rgba(65, 119, 189, 0.3)',
              transition: 'all 0.2s ease'
            }}
          >
            <ReplyIcon size={14} />
          </button>

          <button
            className="message-action-button react-button"
            onClick={(e) => {
              logger.log('😊 REACT BUTTON CLICKED!', { messageId: message._id });
              e.stopPropagation();
              setShowEmojiBar(true);
            }}
            title="React"
            style={{
              background: 'linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%)',
              border: '2px solid #4177bd',
              padding: '6px',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: '#4177bd',
              boxShadow: '0 2px 8px rgba(65, 119, 189, 0.3)',
              transition: 'all 0.2s ease'
            }}
          >
            <ReactIcon size={14} />
          </button>
        </div>
        )}

        {/* Emoji reactions bar */}
        {showEmojiBar && !isOutgoing && (
          <div ref={menuRef} className="emoji-reaction-bar">
            {quickEmojis.map((emoji, index) => (
              <button
                key={index}
                className="emoji-option"
                onClick={(e) => {
                  logger.log('🟢 EMOJI CLICKED!', emoji);
                  e.stopPropagation();
                  handleEmojiClick(emoji);
                  setShowEmojiBar(false);
                }}
                title={`React with ${emoji}`}
              >
                {emoji}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
